import { CHECKOUT_SDK_ERROR_CODES } from '@ekhodealer/ekho-common';

import type { CheckoutModuleApi, EventBus } from '@ekhodealer/ekho-common';
import type { EkhoNamespace, QueuedCall } from './types';

import { CheckoutSdkError } from '../utils/sdk-error';

import { consentStatus } from './consent';
import { MODULE_REGISTRY } from './module-registry';

export interface Deferred {
  promise: Promise<void>;
  resolve: () => void;
  reject: (reason?: unknown) => void;
}

/** A `Promise` with externalized resolve/reject, for `Ekho.ready`. */
function createDeferred(): Deferred {
  let resolve!: () => void;
  let reject!: (reason?: unknown) => void;
  const promise = new Promise<void>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  // Prevent unhandled rejection when the dealer doesn't await `ready`.
  promise.catch(() => {});
  return { promise, resolve, reject };
}

export interface NamespaceHandle {
  ekho: EkhoNamespace;
  ready: Deferred;
  /**
   * Settles as soon as bootstrap has decided checkout's fate, which is well
   * before `ready`. Bootstrap binds checkout straight after the config fetch
   * but resolves `ready` only once every remote module has finished importing
   * and initializing, and those imports have no loader-side timeout. Gating
   * the checkout stubs on `ready` would therefore let a stalled chat or express
   * bundle hold up a buy click that the bound checkout API could already serve.
   */
  checkoutBound: Deferred;
}

/**
 * Pre-bootstrap stubs for the checkout capability, which is built into the
 * loader rather than registered in MODULE_REGISTRY, so nothing generates its
 * stubs for it.
 *
 * The cart and availability calls wait for checkout to bind and then delegate
 * to it. None may reject on its own account: a host calling before the config
 * fetch lands is early, not wrong, and a rejection at that moment is
 * indistinguishable from a real failure, so an integrator's catch block reads a
 * healthy SDK as a dead one and falls back to a non-Ekho path. The wait is
 * bounded by the config fetch's own timeout and retry, after which
 * `checkoutBound` settles either way.
 *
 * The queue cannot carry those, because their callers await a value it has
 * no way to deliver. `redirect` is fire-and-forget, so it queues and replays.
 * `attachAttributionToUrl` is synchronous and cannot wait, so pre-bootstrap it
 * returns the URL unchanged.
 */
function createCheckoutStub(
  getBoundApi: () => CheckoutModuleApi,
  checkoutBound: Promise<void>,
  queue: QueuedCall[],
): CheckoutModuleApi {
  const stub: CheckoutModuleApi = {
    createInventoryCart: async (options) => (await resolveApi()).createInventoryCart(options),
    createBuildToOrderCart: async (options) => (await resolveApi()).createBuildToOrderCart(options),
    validateInventoryCart: async (options) => (await resolveApi()).validateInventoryCart(options),
    validateBuildToOrderCart: async (options) =>
      (await resolveApi()).validateBuildToOrderCart(options),
    createCart: async (options) => (await resolveApi()).createCart(options),
    isAvailable: async (options) => (await resolveApi()).isAvailable(options),
    redirect: (...args: unknown[]) => {
      queue.push({ module: 'checkout', method: 'redirect', args });
    },
    attachAttributionToUrl: (url: string) => url,
  };

  /**
   * A settled `checkoutBound` does not promise checkout was bound: bootstrap
   * leaves it unbound when config disables the SDK or omits the module, and
   * when a transient config failure deactivates the loader. The namespace still
   * holds this stub in each of those cases, so the identity check is what stops
   * a delegation from recursing into itself.
   */
  async function resolveApi(): Promise<CheckoutModuleApi> {
    await checkoutBound;
    const api = getBoundApi();
    if (api === stub) {
      throw new CheckoutSdkError(
        'Ekho.checkout is not enabled for this key',
        CHECKOUT_SDK_ERROR_CODES.CHECKOUT_UNAVAILABLE,
      );
    }
    return api;
  }

  return stub;
}

/**
 * Build `window.Ekho` with queue-backed stubs. Calls before modules load are
 * buffered and replayed after activation. Stubs are generated from MODULE_REGISTRY.
 */
export function createNamespace(bus: EventBus): NamespaceHandle {
  const queue: QueuedCall[] = [];
  const ready = createDeferred();
  const checkoutBound = createDeferred();

  // Backstop, not the primary path: bootstrap settles `checkoutBound` as soon
  // as it knows checkout's fate. Mirroring `ready` onto it means a bootstrap
  // exit that settles only `ready` still releases the stubs, so a path added
  // later that forgets degrades to waiting for full readiness rather than
  // hanging a buy click forever. Settling an already-settled deferred is a
  // no-op, so this never overrides the earlier, more precise outcome.
  void ready.promise.then(
    () => checkoutBound.resolve(),
    (err: unknown) => checkoutBound.reject(err),
  );

  const ekho: EkhoNamespace = {
    ready: ready.promise,
    config: null,
    attribution: null,
    update: (...args: unknown[]) => {
      queue.push({ module: 'core', method: 'update', args });
    },
    destroy: (...args: unknown[]) => {
      queue.push({ module: 'core', method: 'destroy', args });
    },
    // Satisfies static type; chat + express are overwritten by the registry
    // loop below (queue-backed stubs).
    chat: { open: () => {}, destroy: () => {} },
    checkout: createCheckoutStub(() => ekho.checkout, checkoutBound.promise, queue),
    express: { mount: () => {}, unmount: () => {} },
    // Consent is a built-in capability (no descriptor), so hand-roll queue
    // stubs. grant/revoke queue and replay against the real controller after
    // bootstrap; status reads the live gate state (reports `not-required` until
    // initConsent runs).
    consent: {
      grant: (...args: unknown[]) => {
        queue.push({ module: 'consent', method: 'grant', args });
      },
      revoke: (...args: unknown[]) => {
        queue.push({ module: 'consent', method: 'revoke', args });
      },
      status: () => consentStatus(),
    },
    _bus: bus,
    _queue: queue,
  };

  // Generate queue-backed stubs for each registered module's API surface.
  for (const descriptor of MODULE_REGISTRY) {
    const stubs: Record<string, (...args: unknown[]) => void> = {};
    for (const method of Object.keys(descriptor.fallbackApi)) {
      stubs[method] = (...args: unknown[]): void => {
        queue.push({ module: descriptor.name, method, args });
      };
    }
    (ekho as Record<string, unknown>)[descriptor.name] = stubs;
  }

  return { ekho, ready, checkoutBound };
}
